// ————————————————————————————————————————————
// Guest Saved Diamonds (V1)
//
// Independent persistence for Saved Diamonds. Owns localStorage only —
// not search state, filters, sorting, pagination, or media/viewer.
//
// Canonical stored id is IvanovAPI.canonicalId: "DIAMOND/{uuid}".
// Search items[].id and Detail diamond.id already use that form.
// Bare UUIDs, encoded IDs, and prefix-case variants normalize to it.
// Non-UUID legacy ids are preserved as trimmed strings so older
// records are not invalidated. Equivalent ids cannot coexist.
//
// Loaded once from layout/theme.liquid as window.DiamondSaved.
// Search / Detail / future header consumers must not load it again.
//
// A saved record is the snapshot from the moment Save occurred. Reloading
// search results must not rewrite savedAt or snapshot.
// ————————————————————————————————————————————
(function (global) {
  "use strict";

  if (global.DiamondSaved && global.DiamondSaved.STORAGE_KEY === "ivanovSavedDiamonds") {
    return;
  }

  const STORAGE_KEY = "ivanovSavedDiamonds";
  const CHANGE_EVENT = "ivanov:saved-diamonds-change";
  const SCHEMA_VERSION = 1;
  const MAX_ITEMS = 50;
  const CANONICAL_PREFIX = "DIAMOND/";
  const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const SNAPSHOT_KEYS = [
    "origin",
    "shape",
    "carat",
    "cut",
    "color",
    "clarity",
    "lab",
    "certificateNumber",
    "hasGradingReport",
    "retailPriceUsd",
    "image",
    "has360",
    "certificateUrl",
  ];

  function emptyStore() {
    return { schemaVersion: SCHEMA_VERSION, items: [] };
  }

  function decodeOnce(value) {
    try {
      return decodeURIComponent(value);
    } catch (err) {
      return value;
    }
  }

  function normalizeId(id) {
    if (id == null || id === "") return "";
    const raw = String(id).trim();
    if (!raw) return "";
    const decoded = String(decodeOnce(raw)).trim();
    if (!decoded) return "";
    let bare = decoded;
    if (/^diamond\//i.test(decoded)) {
      bare = decoded.replace(/^diamond\//i, "").trim();
    }
    if (UUID_RE.test(bare)) {
      return CANONICAL_PREFIX + bare.toLowerCase();
    }
    return decoded;
  }

  function snapshotScalar(value) {
    if (value == null || value === "") return null;
    const type = typeof value;
    if (type === "string" || type === "number") return value;
    return null;
  }

  function sanitizeSnapshot(input) {
    const src = input && typeof input === "object" ? input : {};
    const out = {};
    SNAPSHOT_KEYS.forEach((key) => {
      if (key === "has360" || key === "hasGradingReport") {
        out[key] = src[key] === true;
      } else {
        out[key] = snapshotScalar(src[key]);
      }
    });
    return out;
  }

  function normalizeItem(raw) {
    if (!raw || typeof raw !== "object") return null;
    const id = normalizeId(raw.id);
    if (!id) return null;
    return {
      id: id,
      savedAt: typeof raw.savedAt === "string" && raw.savedAt ? raw.savedAt : null,
      snapshot: sanitizeSnapshot(raw.snapshot),
    };
  }

  function getStorage() {
    try {
      const storage = global.localStorage;
      if (!storage) return null;
      const probe = "__ivanov_saved_probe__";
      storage.setItem(probe, "1");
      storage.removeItem(probe);
      return storage;
    } catch (err) {
      return null;
    }
  }

  function readStoreDetailed() {
    const storage = getStorage();
    if (!storage) return { store: emptyStore(), shouldPersist: false };
    try {
      const raw = storage.getItem(STORAGE_KEY);
      if (raw == null || raw === "") return { store: emptyStore(), shouldPersist: false };
      const parsed = JSON.parse(raw);
      if (
        !parsed ||
        typeof parsed !== "object" ||
        parsed.schemaVersion !== SCHEMA_VERSION ||
        !Array.isArray(parsed.items)
      ) {
        return { store: emptyStore(), shouldPersist: false };
      }
      const items = [];
      const seen = Object.create(null);
      let changed = false;
      parsed.items.forEach((rawItem) => {
        const item = normalizeItem(rawItem);
        if (!item) {
          changed = true;
          return;
        }
        if (rawItem && normalizeId(rawItem.id) === item.id && rawItem.id !== item.id) {
          changed = true;
        }
        if (seen[item.id]) {
          changed = true;
          return;
        }
        seen[item.id] = true;
        items.push(item);
      });
      return {
        store: { schemaVersion: SCHEMA_VERSION, items: items },
        shouldPersist: changed,
      };
    } catch (err) {
      return { store: emptyStore(), shouldPersist: false };
    }
  }

  function readStore() {
    return readStoreDetailed().store;
  }

  function writeStore() {
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          schemaVersion: SCHEMA_VERSION,
          items: memory.items,
        })
      );
    } catch (err) {
      // Quota / private-mode write failures must not throw.
    }
  }

  function createChangeEvent(detail) {
    if (typeof global.CustomEvent === "function") {
      return new global.CustomEvent(CHANGE_EVENT, { detail: detail });
    }
    return { type: CHANGE_EVENT, detail: detail };
  }

  function dispatchChange(action, id) {
    syncRenderedHearts();
    if (typeof global.dispatchEvent !== "function") return;
    try {
      global.dispatchEvent(
        createChangeEvent({
          action: action,
          id: id || null,
          count: memory.items.length,
          items: list(),
        })
      );
    } catch (err) {
      // Event dispatch must never break save/remove.
    }
  }

  const initial = readStoreDetailed();
  let memory = initial.store;
  if (initial.shouldPersist) writeStore();

  function findIndex(id) {
    const key = normalizeId(id);
    if (!key) return -1;
    for (let i = 0; i < memory.items.length; i += 1) {
      if (memory.items[i].id === key) return i;
    }
    return -1;
  }

  function has(id) {
    return findIndex(id) >= 0;
  }

  function count() {
    return memory.items.length;
  }

  function list() {
    return memory.items.map((item) => ({
      id: item.id,
      savedAt: item.savedAt,
      snapshot: Object.assign({}, item.snapshot),
    }));
  }

  function save(record) {
    const id = normalizeId(record && record.id);
    if (!id) return null;
    const existingIndex = findIndex(id);
    if (existingIndex >= 0) {
      return memory.items[existingIndex];
    }
    const item = {
      id: id,
      savedAt: new Date().toISOString(),
      snapshot: sanitizeSnapshot(record && record.snapshot),
    };
    while (memory.items.length >= MAX_ITEMS) {
      memory.items.shift();
    }
    memory.items.push(item);
    writeStore();
    dispatchChange("save", id);
    return item;
  }

  function remove(id) {
    const key = normalizeId(id);
    const index = findIndex(key);
    if (index < 0) return false;
    memory.items.splice(index, 1);
    writeStore();
    dispatchChange("remove", key);
    return true;
  }

  function toggle(record) {
    const id = normalizeId(record && record.id);
    if (!id) return false;
    if (has(id)) {
      remove(id);
      return false;
    }
    save(record);
    return has(id);
  }

  function inferHas360(diamond) {
    diamond = diamond || {};
    try {
      const Media = global.DiamondCardMedia;
      if (Media && typeof Media.hasInteractive360 === "function") {
        const media =
          typeof Media.resolveDiamondMedia === "function"
            ? Media.resolveDiamondMedia(diamond)
            : diamond;
        return Media.hasInteractive360(media) === true;
      }
    } catch (err) {
      // Fall through to a local boolean inference.
    }
    const v360 = diamond.v360;
    const prepared = diamond.preparedTurntable;
    return Boolean(
      (v360 && v360.url) ||
        (prepared &&
          prepared.available === true &&
          typeof prepared.manifestUrl === "string" &&
          prepared.manifestUrl)
    );
  }

  function buildSnapshot(diamond) {
    diamond = diamond || {};
    return sanitizeSnapshot({
      origin: diamond.origin,
      shape: diamond.shape,
      carat: diamond.carat,
      cut: diamond.cut,
      color: diamond.color,
      clarity: diamond.clarity,
      lab: diamond.lab,
      certificateNumber: diamond.certificateNumber,
      hasGradingReport: diamond.hasGradingReport === true,
      retailPriceUsd: diamond.retailPriceUsd,
      image: diamond.image,
      has360: inferHas360(diamond),
      certificateUrl: diamond.certificateUrl,
    });
  }

  function applyHeartState(button, isSaved) {
    if (!button) return isSaved === true;
    const saved = isSaved === true;
    if (button.classList) {
      if (saved) button.classList.add("is-active");
      else button.classList.remove("is-active");
    }
    if (typeof button.setAttribute === "function") {
      button.setAttribute("aria-pressed", saved ? "true" : "false");
      button.setAttribute(
        "aria-label",
        saved ? "Remove saved diamond" : "Save diamond"
      );
    }
    if (typeof button.querySelector === "function") {
      const label = button.querySelector("[data-save-label]");
      if (label) label.textContent = saved ? "SAVED" : "SAVE";
    }
    return saved;
  }

  function isHeartTarget(target) {
    if (!target) return false;
    if (typeof target.closest === "function") {
      return Boolean(target.closest(".diamond-card__favorite"));
    }
    return !!(
      target.classList &&
      typeof target.classList.contains === "function" &&
      target.classList.contains("diamond-card__favorite")
    );
  }

  function isolateHeartKeyboard(event) {
    if (!event) return false;
    if (event.key !== "Enter" && event.key !== " ") return false;
    if (typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
    return true;
  }

  function buttonRecordId(node) {
    if (!node) return "";
    if (node.dataset && node.dataset.diamondId) return node.dataset.diamondId;
    if (typeof node.getAttribute === "function") {
      return node.getAttribute("data-diamond-id") || "";
    }
    return "";
  }

  function syncRenderedHearts(root) {
    const scope = root || global.document;
    if (!scope || typeof scope.querySelectorAll !== "function") return;
    const cards = scope.querySelectorAll(".diamond-card[data-diamond-id]");
    const cardList =
      cards && typeof cards.forEach === "function"
        ? cards
        : Array.prototype.slice.call(cards || []);
    cardList.forEach((card) => {
      if (!card) return;
      const id = buttonRecordId(card);
      const btn =
        typeof card.querySelector === "function"
          ? card.querySelector(".diamond-card__favorite")
          : null;
      if (btn) applyHeartState(btn, has(id));
    });
    const saves = scope.querySelectorAll("[data-save]");
    const saveList =
      saves && typeof saves.forEach === "function"
        ? saves
        : Array.prototype.slice.call(saves || []);
    saveList.forEach((button) => {
      if (!button) return;
      applyHeartState(button, has(buttonRecordId(button)));
    });
  }

  function onStorageEvent(event) {
    if (!event) return;
    if (event.key != null && event.key !== STORAGE_KEY) return;
    memory = readStore();
    dispatchChange("sync", null);
  }

  if (typeof global.addEventListener === "function") {
    global.addEventListener("storage", onStorageEvent);
  }

  global.DiamondSaved = {
    STORAGE_KEY: STORAGE_KEY,
    CHANGE_EVENT: CHANGE_EVENT,
    SCHEMA_VERSION: SCHEMA_VERSION,
    MAX_ITEMS: MAX_ITEMS,
    SNAPSHOT_KEYS: SNAPSHOT_KEYS.slice(),
    normalizeId: normalizeId,
    has: has,
    count: count,
    save: save,
    remove: remove,
    toggle: toggle,
    list: list,
    buildSnapshot: buildSnapshot,
    applyHeartState: applyHeartState,
    isHeartTarget: isHeartTarget,
    isolateHeartKeyboard: isolateHeartKeyboard,
    syncRenderedHearts: syncRenderedHearts,
  };
})(typeof window !== "undefined" ? window : this);
